import { useAuth0 } from "@auth0/auth0-react";
import { ErrorBoundary, H, HighlightInit } from "@highlight-run/next/client";
import { Router } from "next/router";
import Script from "next/script";
import React, { useEffect } from "react";

import { IUserRole } from "../generated/types";
import { Segment } from "./segment";

const HIGHLIGHT_RUN_PROJECT_ID = process.env.NEXT_PUBLIC_HIGHLIGHT_RUN_PROJECT_ID;
const SEGMENT_WRITE_KEY = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID;
const API_URL = process.env.NEXT_PUBLIC_API_URL;

// Set up Segment page tracking
Router.events.on("routeChangeComplete", (url) => {
    // Don't track our uptime checker pings
    if (url === "/ping") {
        return;
    }

    // Don't track redirects from Auth0
    if (url.includes("?code=")) {
        return;
    }

    Segment.page();
});

interface IProps {
    role: IUserRole | undefined;
    orgId: string | undefined;
}

// Note that this must be called within AuthProvider to get the user
export const UserAnalyticsServicesProvider: React.FC<React.PropsWithChildren<IProps>> = ({ orgId, children }) => {
    const { user } = useAuth0();
    const userSub = user?.sub;

    useEffect(() => {
        if (userSub != null) {
            if (HIGHLIGHT_RUN_PROJECT_ID) {
                H.identify(userSub, orgId ? { orgId } : undefined);
            }
            if (SEGMENT_WRITE_KEY) {
                Segment.identify(userSub, { orgId });
            }
        }
    }, [userSub, orgId]);

    return (
        <ErrorBoundary showDialog={false}>
            {HIGHLIGHT_RUN_PROJECT_ID && (
                <HighlightInit
                    privacySetting="default"
                    projectId={HIGHLIGHT_RUN_PROJECT_ID}
                    tracingOrigins={API_URL ? [API_URL] : undefined}
                    networkRecording={{
                        enabled: true,
                        recordHeadersAndBody: true,
                        // redact all user-provided data for now
                        headerKeysToRecord: ["Levro-Org-Id"],
                        bodyKeysToRecord: ["operationName", "query"],
                    }}
                    version={BUILD_ID}
                />
            )}
            {SEGMENT_WRITE_KEY && (
                <Script
                    id="segment"
                    dangerouslySetInnerHTML={{
                        __html: `
                            !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://seg-cdn.levro.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${SEGMENT_WRITE_KEY}";;analytics.SNIPPET_VERSION="4.15.3";
                            analytics.load("${SEGMENT_WRITE_KEY}");
                            // We comment out the analytics.page() call below because due to SSR it would double count pages
                            // analytics.page();
                            }}();`,
                    }}
                />
            )}
            {children}
        </ErrorBoundary>
    );
};
